'use client';

import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import InternalStepper, {
  InternalStep,
  InternalStepperRef,
} from '../components/InternalStepper';
import { InternalStepValidationProvider } from '../context/InternalStepValidationContext';
import PropertyFinder from './PropertyFinder';
import PropertyReview from './PropertyReview';
import PropertyAddonRecommendations from './PropertyAddonRecommendations';
import PropertyMoreInfo from './PropertyMoreInfo';
import { useEffect, useRef, useState, useMemo } from 'react';
import callStreetView from '@/util/functions/fetching/callStreetView';
import { fetchBridgeData } from '@/util/functions/fetching/callZillowAndMatch';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { RedactedServiceArea } from '@/util/types/optimalSlotType';
import { Service, Bundle, RequiredInfo } from '@/util/types/serverTypes';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  extractPropertyDetails,
  extractAddressData,
} from '@/util/functions/property/extractPropertyDetails';
import { useStartOver } from '../hooks/useStartOver';
import { useWhatConvertsEvents } from '../hooks/useWhatConvertsEvents';
import { hasMissingRequiredPropertyData } from './propertyRequirements';

export default function PropertyStep({
  allServices,
  bundles,
  allRequiredInfo,
}: {
  allServices: Service[];
  bundles: Bundle[];
  allRequiredInfo: RequiredInfo[];
}) {
  const {
    address,
    setAddress,
    streetViewUrl,
    setStreetViewUrl,
    zillowPropertyData,
    setZillowPropertyData,
    containingServiceAreaArray,
    setContainingServiceAreaArray,
    propertyDetails,
    setPropertyDetails,
    selectedPrimaryService,
    setSelectedRole,
    requiredInfoValues,
    jwtToken,
    companyId,
    contact,
    draftQuoteId,
    wcLeadId,
    setWcLeadId,
    markPropertyPageSeen,
    isLoadingDraft,
  } = useScheduler();
  const { createLead } = useWhatConvertsEvents();
  const { nextStep } = useStepNavigation();
  const internalStepperRef = useRef<InternalStepperRef>(null);
  const [prevAddressLocation, setPrevAddressLocation] = useState<{
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    lat?: number;
    lng?: number;
  } | null>(null);
  const hasInitializedLocationRef = useRef(false);
  const [hasVisitedReviewStep, setHasVisitedReviewStep] = useState(false); // Track if user has visited step 1 (Review Property Details)
  const [shouldAutoAdvanceToReview, setShouldAutoAdvanceToReview] =
    useState(false); // Track if we should auto-advance after fetching new property data
  const hasCreatedLeadRef = useRef(false); // Track if WhatConverts lead has been created

  const handleStartOver = useStartOver({
    navigateToStep: 1,
    deferNavigation: true,
    onBeforeReset: () => {
      // Clear step-specific local state
      setPrevAddressLocation(null);
      setHasVisitedReviewStep(false); // Reset review step tracking
      setShouldAutoAdvanceToReview(false); // Reset auto-advance flag
      // Clear selectedRole so services step starts at step 0 (Filter Form) instead of step 1
      setSelectedRole(null);
    },
  });

  // If resuming from a draft and user has already completed required info,
  // mark step 1 as visited (they've already reviewed property details)
  useEffect(() => {
    if (requiredInfoValues.length > 0) {
      setHasVisitedReviewStep(true);
    }
  }, [requiredInfoValues.length]);

  // Check if primary service has addon recommendations (needed for initial step calculation)
  const primaryService = allServices.find(
    (s) => s._id === selectedPrimaryService
  );
  const addonRecommendationIds = primaryService?._addonRecommendationIds || [];

  // Check if there are actually available addon services (not just IDs)
  // Addon services might be inactive or deleted, so we need to verify they exist
  const availableAddonServices = allServices.filter((s) =>
    addonRecommendationIds.includes(s._id)
  );
  const hasAvailableAddons = availableAddonServices.length > 0;

  // Only show addon step if:
  // 1. There are available addon services AND
  // 2. The primary service has aiUpsellAddons enabled
  const shouldShowAddonStep =
    hasAvailableAddons && primaryService?.aiUpsellAddons === true;

  /**
   * When no upsell page is part of this flow, report it seen so the step gate
   * does not wait for a page that will never render — including the case where
   * the selected service has dropped out of the catalog, where waiting would
   * leave the customer unable to book at all.
   *
   * Held until any draft has finished hydrating: `selectedPrimaryService` is
   * briefly absent mid-load, and `shouldShowAddonStep` reads false then for want
   * of data rather than because there is no upsell.
   */
  useEffect(() => {
    if (isLoadingDraft || shouldShowAddonStep) return;
    markPropertyPageSeen('addons');
  }, [isLoadingDraft, shouldShowAddonStep, markPropertyPageSeen]);

  // Calculate initial step based on context data (for draft rehydration)
  // This MUST be reactive to context so it recalculates when DraftLoader populates data
  // CRITICAL: Always show step 1 (Review Property Details) when propertyDetails are auto-populated
  // unless the user has already visited step 1 (draft resume) or completed required info
  const initialStep = useMemo(() => {
    // Step 0: Find Property - if no address
    if (!address || !address.street) {
      return 0;
    }

    // Step 1: Review Property Details - ALWAYS show if user hasn't visited it yet
    // This ensures users see auto-populated Zillow data even if propertyDetails exists
    if (!hasVisitedReviewStep) {
      return 1;
    }

    // A resumed draft can carry an address saved before the required-detail rule
    // applied, or one the customer abandoned half-filled. Open on the step that
    // holds the problem rather than on a later step whose Next button is locked
    // for reasons the customer cannot see from there.
    if (hasMissingRequiredPropertyData(address)) {
      return 1;
    }

    // If we have property details, check if we need to show addon recommendations
    // Step 2: Addon Recommendations (if applicable)
    // Step 2 or 3: More Information - skip to last step if all previous steps complete
    if (requiredInfoValues.length > 0) {
      // All steps complete, go to last step
      const lastStep = shouldShowAddonStep ? 3 : 2;
      return lastStep;
    }

    // Has property details but no required info yet - go to addon step or more info
    if (shouldShowAddonStep) {
      return 2;
    } else {
      return 2;
    }
  }, [
    address,
    propertyDetails,
    requiredInfoValues,
    shouldShowAddonStep,
    hasVisitedReviewStep,
  ]); // React to context changes!

  // Fetch property data when address changes
  useEffect(() => {
    // Check if we have a valid address (either formatted string or individual fields)
    const hasAddressString = !!address?.address;
    const hasAddressFields = !!(
      address?.street &&
      address?.city &&
      address?.state &&
      address?.zip
    );
    const hasValidAddress = hasAddressString || hasAddressFields;

    // Construct formatted address if missing but we have individual fields
    let formattedAddress = address?.address;
    if (!formattedAddress && hasAddressFields && address) {
      formattedAddress = `${address.street}${
        address.unit ? ` ${address.unit}` : ''
      }, ${address.city}, ${address.state} ${address.zip}`;
    }

    if (!hasValidAddress) {
      setStreetViewUrl(null);
      setZillowPropertyData(null);
      setContainingServiceAreaArray(null);
      setPropertyDetails(null);
      // Clear location tracker so next address will be detected as a change
      setPrevAddressLocation(null);
      hasInitializedLocationRef.current = false;
      return;
    }

    // Update address in context if we constructed the formatted address field
    if (formattedAddress && address && !address.address) {
      setAddress({
        ...address,
        address: formattedAddress,
      });
    }

    let isCancelled = false;

    // Check if location changed first
    const locationChanged = hasAddressLocationChanged(
      prevAddressLocation,
      address
    );

    // Initialize prevAddressLocation from current address if not set (e.g., when navigating back)
    // This prevents unnecessary refetches when data already exists in context
    // Use ref to track initialization to avoid dependency loop
    if (
      !hasInitializedLocationRef.current &&
      address &&
      zillowPropertyData &&
      !locationChanged
    ) {
      // We have cached data and are navigating back - initialize tracker to prevent refetch
      setPrevAddressLocation({
        street: address.street,
        city: address.city,
        state: address.state,
        zip: address.zip,
        lat: address.lat,
        lng: address.lng,
      });
      hasInitializedLocationRef.current = true;
    }

    // Reset initialization ref if address actually changed (new property selected)
    if (locationChanged && hasInitializedLocationRef.current) {
      hasInitializedLocationRef.current = false;
    }

    // SYNCHRONOUS: If location changed (new property selected), clear old data and reset flags IMMEDIATELY
    // This must happen synchronously BEFORE any async operations to prevent race conditions
    if (locationChanged) {
      // Clear all property-related data from the previous property
      setPropertyDetails(null);
      setZillowPropertyData(null);
      setStreetViewUrl(null);
      setContainingServiceAreaArray(null);
      // Reset flags
      setHasVisitedReviewStep(false);
      setShouldAutoAdvanceToReview(true);
    }

    // CRITICAL: Extract property details synchronously from cached data BEFORE async function
    // This prevents loading states from showing when navigating back with cached data
    // Only run this if location has NOT changed (we're using cached data for same property)
    if (zillowPropertyData && !propertyDetails && !locationChanged) {
      const extractedPropertyDetails =
        extractPropertyDetails(zillowPropertyData);
      setPropertyDetails(extractedPropertyDetails);

      // Also ensure address fields are populated from cached data
      const addressData = extractAddressData(zillowPropertyData);
      setAddress((currentAddress: AddressForScheduler | null) => {
        if (!currentAddress) return currentAddress;
        return {
          ...currentAddress,
          sqft: currentAddress.sqft || addressData.sqft,
          yearBuilt: currentAddress.yearBuilt || addressData.yearBuilt,
          unitCount: currentAddress.unitCount ?? addressData.unitCount,
          foundation: currentAddress.foundation || addressData.foundation,
          county: addressData.county || currentAddress.county,
          sqftVerified: currentAddress.sqft
            ? currentAddress.sqftVerified
            : false,
          yearBuiltVerified: currentAddress.yearBuilt
            ? currentAddress.yearBuiltVerified
            : false,
          unitCountVerified:
            currentAddress.unitCount !== undefined
              ? currentAddress.unitCountVerified
              : false,
          foundationVerified: currentAddress.foundation
            ? currentAddress.foundationVerified
            : false,
        };
      });
    }

    const fetchPropertyData = async () => {
      try {
        // EARLY RETURN: Skip all fetching if we have all cached data and location hasn't changed
        // This prevents loading states from showing when navigating back with cached data
        // Note: containingServiceAreaArray can be [] (empty array) which is still valid cached data
        const hasCachedServiceArea =
          containingServiceAreaArray !== null &&
          containingServiceAreaArray !== undefined;
        if (
          !locationChanged &&
          zillowPropertyData &&
          streetViewUrl &&
          hasCachedServiceArea
        ) {
          return;
        }

        // Step 1: Set street view URL if location changed or not cached
        if (locationChanged || !streetViewUrl) {
          if (!isCancelled) setStreetViewUrl(callStreetView(formattedAddress!));
        }

        // Step 2: Determine if we need fresh Zillow data or can use cache
        let currentZillowData = zillowPropertyData;

        // If location changed, always fetch fresh data
        // Note: We cleared zillowPropertyData synchronously above if location changed,
        // but we use locationChanged flag directly to avoid closure stale value issues
        if (locationChanged) {
          const zillow = await fetchBridgeData(
            address.lat,
            address.lng,
            formattedAddress!
          );
          if (isCancelled) return;

          const addressData = extractAddressData(zillow);
          currentZillowData = {
            ...zillow,
            totalFinishedSqft: addressData.sqft,
            yearBuilt: addressData.yearBuilt,
            unitCount: addressData.unitCount,
          } as any;

          setZillowPropertyData(currentZillowData);

          // Update location tracker after successful fetch
          setPrevAddressLocation({
            street: address.street,
            city: address.city,
            state: address.state,
            zip: address.zip,
            lat: address.lat,
            lng: address.lng,
          });
          hasInitializedLocationRef.current = true;
        } else {
          // Location hasn't changed, use cached data
          currentZillowData = zillowPropertyData;
        }

        // Step 3: Fetch service area if location changed or not cached
        if (locationChanged || !containingServiceAreaArray) {
          const foundArea = await callServerAction<RedactedServiceArea[]>({
            url: 'service-area/find-containing',
            params: {
              lat: address.lat,
              lng: address.lng,
              _companyId: companyId,
            },
            headers: {
              'client-token': jwtToken,
            },
          });
          if (isCancelled) return;

          if (foundArea.length > 0) {
            setContainingServiceAreaArray(
              foundArea.map((area) => ({
                inside: true,
                serviceArea: area,
              }))
            );
          } else {
            setContainingServiceAreaArray([]);
          }
        }

        // Step 4: Extract and set property details if we have Zillow data
        if (currentZillowData && !propertyDetails) {
          const extractedPropertyDetails =
            extractPropertyDetails(currentZillowData);
          setPropertyDetails(extractedPropertyDetails);

          // Step 5: Auto-populate address fields from Zillow data
          const addressData = extractAddressData(currentZillowData);
          setAddress((currentAddress: AddressForScheduler | null) => {
            if (!currentAddress) return currentAddress;
            return {
              ...currentAddress,
              sqft: currentAddress.sqft || addressData.sqft,
              yearBuilt: currentAddress.yearBuilt || addressData.yearBuilt,
              unitCount: currentAddress.unitCount ?? addressData.unitCount,
              foundation: currentAddress.foundation || addressData.foundation,
              county: addressData.county || currentAddress.county,
              sqftVerified: currentAddress.sqft
                ? currentAddress.sqftVerified
                : false,
              yearBuiltVerified: currentAddress.yearBuilt
                ? currentAddress.yearBuiltVerified
                : false,
              unitCountVerified:
                currentAddress.unitCount !== undefined
                  ? currentAddress.unitCountVerified
                  : false,
              foundationVerified: currentAddress.foundation
                ? currentAddress.foundationVerified
                : false,
            };
          });
        }
      } catch (error) {
        console.error('Error fetching property data:', error);
      }
    };

    fetchPropertyData();

    return () => {
      isCancelled = true;
    };
    // CRITICAL: Only depend on address - other values (zillowPropertyData, propertyDetails, streetViewUrl)
    // are OUTPUTS of this effect, not inputs. Including them causes infinite loops or unnecessary re-runs.
    // prevAddressLocation is local state we track for comparison, so it's fine to include.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    prevAddressLocation,
    // Note: containingServiceAreaArray is intentionally NOT in deps
    // We guard against it with `if (!containingServiceAreaArray)` inside
    // Note: setAddress, setStreetViewUrl, etc. are stable and don't need to be in deps
  ]);

  // Auto-advance to Review step when new property data is ready
  useEffect(() => {
    if (
      shouldAutoAdvanceToReview &&
      propertyDetails &&
      internalStepperRef.current
    ) {
      internalStepperRef.current.setActiveStep(1);
      setShouldAutoAdvanceToReview(false);
    }
  }, [shouldAutoAdvanceToReview, propertyDetails]);

  // Create WhatConverts lead once when we have contact info and quote ID
  // Skip if lead already exists (e.g., from resumed draft)
  useEffect(() => {
    if (hasCreatedLeadRef.current) return;
    if (wcLeadId) {
      // Lead already exists (resumed draft) - mark as created to prevent duplicate
      hasCreatedLeadRef.current = true;
      return;
    }
    if (!contact?.email || !draftQuoteId) return;

    hasCreatedLeadRef.current = true;

    const createWcLead = async () => {
      const leadId = await createLead({
        quoteId: draftQuoteId,
        email: contact.email,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
      });
      if (leadId) {
        setWcLeadId(leadId);
      }
    };

    createWcLead();
  }, [contact, draftQuoteId, wcLeadId, createLead, setWcLeadId]);

  const handleContinueToRecommendations = () => {
    // Mark that user has visited step 1 (Review Property Details)
    setHasVisitedReviewStep(true);

    if (internalStepperRef.current) {
      // Always go to step 2, which is either addon recommendations or more info
      internalStepperRef.current.setActiveStep(2);
    }
  };

  const handleContinueToMoreInfo = () => {
    if (internalStepperRef.current) {
      // From addon step (step 2) to more info (step 3)
      internalStepperRef.current.setActiveStep(3);
    }
  };

  const steps: InternalStep[] = [
    {
      id: 'property_search',
      label: 'Find Your Property',
      content: <PropertyFinder />,
    },
    {
      id: 'property_review',
      label: 'Review Property Details',
      content: <PropertyReview onContinue={handleContinueToRecommendations} />,
    },
    ...(shouldShowAddonStep
      ? [
          {
            id: 'ai_recommendations',
            label: 'Recommended Services',
            content: (
              <PropertyAddonRecommendations
                allServices={allServices}
                bundles={bundles}
                onContinue={handleContinueToMoreInfo}
              />
            ),
          },
        ]
      : []),
    {
      id: 'property_more_info',
      label: 'More Information',
      content: (
        <PropertyMoreInfo
          allRequiredInfo={allRequiredInfo}
          allServices={allServices}
          bundles={bundles}
          onContinue={nextStep}
        />
      ),
    },
  ];

  return (
    <div className='min-h-[600px]'>
      <InternalStepValidationProvider
        mainStep={2}
        totalSteps={steps.length}
        hasAddonStep={shouldShowAddonStep}
      >
        <InternalStepper
          ref={internalStepperRef}
          steps={steps}
          initialStep={initialStep}
          onStartOver={handleStartOver}
          trackingStep='property'
        />
      </InternalStepValidationProvider>
    </div>
  );
}

// Helper function to check if address location has changed
const hasAddressLocationChanged = (
  prev: {
    lat?: number;
    lng?: number;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null,
  current: AddressForScheduler
) => {
  if (!prev) return true;
  return (
    prev.lat !== current.lat ||
    prev.lng !== current.lng ||
    prev.street !== current.street ||
    prev.city !== current.city ||
    prev.state !== current.state ||
    prev.zip !== current.zip
  );
};
